import { StrictMode } from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import ReactGA from "react-ga4";
import * as Sentry from "@sentry/react";
import "./index.css";
import "./i18n";
import Router from "./components/Router.tsx";
import { sidebarStateManager, determineActiveSidebar } from "./utils/SidebarStateManager";
import { initWebVitals } from "./utils/webVitals";

// Initialize Sentry
Sentry.init({
  dsn: "https://9ba8350d355c65a373e7b0e0913a8749@o4509426141691904.ingest.de.sentry.io/4509426146672720",
  sendDefaultPii: true
});

const GA_MEASUREMENT_ID = "G-4L0PKSECEM";
if (typeof window !== "undefined") {
  // send_page_view: false — the gtag config call would otherwise emit its own
  // automatic page_view on top of the explicit one below. SPA navigations are
  // tracked by Router's popstate handler.
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gtagOptions: { send_page_view: false },
  });
  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname + window.location.search,
  });
  initWebVitals();
}

// Read server-injected data to avoid refetch on hydration
const initialData = (window as any).__INITIAL_DATA__;

// Seed the sidebar singleton with the same state the server used so the
// first client render matches the SSR'd HTML — avoids hydration mismatch
// on routes that auto-open a sidebar (restaurant, city, province, etc.).
const initialSidebar = determineActiveSidebar(window.location.pathname, initialData);
if (initialSidebar) sidebarStateManager.openSidebar(initialSidebar);

const rootElement = document.getElementById("root")!;

const app = (
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>} showDialog>
      <Router initialData={initialData} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);

// If the root has SSR content, hydrate; otherwise create fresh root
if (rootElement.innerHTML.trim()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
